import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MockedProvider } from "@apollo/client/testing";
import { ChatInterface } from "../components/chat";
import { type Message } from "../components/chat/types";
import { SEND_MESSAGE_MUTATION } from "../graphql/mutations";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";

// Mock data for demonstration
const mockMessages: Message[] = [
  {
    id: "msg-1",
    conversationId: "demo-conversation",
    senderRole: "AGENT",
    content: {
      text: "Hello! I'm your Personal Assistant. How can I help you today?",
    },
    timestamp: "2024-01-15T10:00:00Z",
  },
  {
    id: "msg-2",
    conversationId: "demo-conversation",
    senderRole: "USER",
    content: { text: "Hi! I need help with planning my day." },
    timestamp: "2024-01-15T10:01:00Z",
  },
  {
    id: "msg-3",
    conversationId: "demo-conversation",
    senderRole: "AGENT",
    content: {
      text: "I'd be happy to help you plan your day! What are your main priorities or tasks you need to accomplish?",
    },
    timestamp: "2024-01-15T10:01:30Z",
  },
];

// Mock GraphQL responses
const mocks = [
  {
    request: {
      query: SEND_MESSAGE_MUTATION,
      variables: {
        conversationId: "demo-conversation",
        content: "This is a test message",
      },
    },
    result: {
      data: {
        sendMessage: {
          message: {
            id: "new-msg-id",
            conversationId: "demo-conversation",
            senderRole: "USER",
            content: { text: "This is a test message" },
            timestamp: new Date().toISOString(),
            metadata: {},
          },
          success: true,
          errorMessage: null,
        },
      },
    },
  },
];

export const ChatDemo: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>(mockMessages);

  const handleMessageSent = (newMessage: Message) => {
    setMessages(prev => [...prev, newMessage]);

    // Simulate assistant response after a delay with more realistic responses
    setTimeout(() => {
      const responses = [
        "I can help you with that! Let me break down your tasks and prioritize them based on urgency and importance.",
        "Great question! I'll analyze your schedule and suggest the most efficient approach to tackle your goals.",
        "I understand. Let me help you automate this process so you don't have to worry about it again.",
        "Perfect! I can set up automated workflows for similar tasks in the future to save you time.",
        "I'll take care of that for you. I can also create reminders and follow-ups to ensure nothing falls through the cracks."
      ];

      const randomResponse = responses[Math.floor(Math.random() * responses.length)];

      const assistantResponse: Message = {
        id: `response-${Date.now()}`,
        conversationId: newMessage.conversationId,
        senderRole: "AGENT",
        content: {
          text: randomResponse,
        },
        timestamp: new Date().toISOString(),
        metadata: {},
      };
      setMessages(prev => [...prev, assistantResponse]);
    }, 1500);
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleGetStarted = () => {
    navigate("/pa/sign-up");
  };

  return (
    <MockedProvider mocks={mocks} addTypename={false}>
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleBackToHome}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">Experience VEDAVIVI in Action</h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              See how our AI Personal Assistant can help you manage tasks, schedule meetings, and automate your daily workflow.
              Try sending a message below to experience the conversation flow.
            </p>
          </div>

          <ChatInterface
            conversationId="demo-conversation"
            assistantName="Demo Assistant"
            messages={messages}
            onMessageSent={handleMessageSent}
          />

          <div className="mt-8 text-center">
            <div className="bg-muted/50 rounded-lg p-6 mb-6">
              <p className="text-sm text-muted-foreground mb-4">
                💡 <strong>Try asking about:</strong> "Help me plan my morning routine" or "Schedule a meeting with my team"
              </p>
              <p className="text-xs text-muted-foreground">
                This is a demo environment. The assistant will respond automatically to showcase the conversation experience.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={handleBackToHome} variant="outline">
                ← Back to Home
              </Button>
              <Button onClick={handleGetStarted} className="bg-teal-500 hover:bg-teal-600">
                Get Started for Free
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MockedProvider>
  );
};
